import {
  buildSessionLifetimeWarningContent,
  SESSION_WARNING_DESCRIPTION_ID,
  SESSION_WARNING_STATES,
  SESSION_WARNING_TITLE_ID,
  sessionLifetimeRemainingLabel,
} from './content';

const CONFIG_SELECTOR = 'meta[name="session-lifetime-config"]';
const MODAL_ID = 'session-lifetime-warning-modal';
const RETRY_DELAY_MS = 60 * 1000;
const MAX_STATUS_DELAY_MS = 60 * 60 * 1000;
const COUNTDOWN_INTERVAL_MS = 1000;
const WARNING_PHASES = [
  SESSION_WARNING_STATES.HARD_60,
  SESSION_WARNING_STATES.HARD_10,
];

class SessionLifetimeWarning {
  constructor(configElement) {
    this.config = {
      authorityNamespace: configElement.dataset.authorityNamespace,
      returnTo: configElement.dataset.returnTo,
      signOutMethod: configElement.dataset.signOutMethod,
      signOutUrl: configElement.dataset.signOutUrl,
      statusUrl: configElement.dataset.statusUrl,
    };
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    this.statusTimer = null;
    this.countdownTimer = null;
    this.fetchingStatus = false;
    this.pendingStatusRequest = false;
    this.currentStatus = null;
    this.modalState = null;
    this.pendingEnded = false;
    this.previouslyFocusedElement = null;
    this.suppressDismissalOnClose = false;

    this.requestStatus = this.requestStatus.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleModalClosed = this.handleModalClosed.bind(this);
    this.handleWarningKeydown = this.handleWarningKeydown.bind(this);
  }

  start() {
    if (
      !this.config.authorityNamespace ||
      !this.config.statusUrl ||
      !this.config.signOutUrl ||
      !this.config.signOutMethod
    ) return;

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('sm-modal:closed', this.handleModalClosed);
    window.addEventListener('focus', this.requestStatus);
    this.requestStatus();
  }

  async requestStatus() {
    if (this.fetchingStatus) {
      this.pendingStatusRequest = true;
      return;
    }

    window.clearTimeout(this.statusTimer);
    this.fetchingStatus = true;

    try {
      const response = await fetch(this.config.statusUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {Accept: 'application/json'},
      });

      if (!response.redirected && response.status === 404) {
        this.stop();
        this.closeWarning();
        return;
      }
      if (!response.redirected && response.status === 401) {
        this.showEnded();
        return;
      }
      if (response.redirected || !response.ok) {
        this.scheduleStatus(RETRY_DELAY_MS);
        return;
      }

      this.handleStatusPayload(await response.json());
    } catch {
      this.scheduleStatus(RETRY_DELAY_MS);
    } finally {
      this.fetchingStatus = false;
      if (this.pendingStatusRequest) {
        this.pendingStatusRequest = false;
        this.requestStatus();
      }
    }
  }

  handleStatusPayload(payload) {
    const status = this.parseStatusPayload(payload);
    if (!status) {
      this.scheduleStatus(RETRY_DELAY_MS);
      return;
    }

    this.currentStatus = status;
    if (status.warningPhase && !this.dismissed(status.warningPhase)) {
      this.showWarning(status.warningPhase);
    } else {
      this.closeWarning();
    }
    this.scheduleStatus(this.remainingUntilMs(status.nextCheckAtMs, status));
  }

  parseStatusPayload(payload) {
    const serverTimeMs = Date.parse(payload.server_time);
    const absoluteExpiresAtMs = Date.parse(payload.absolute_expires_at);
    const nextCheckAtMs = Date.parse(payload.next_check_at);
    const warningPhase = payload.warning_phase || null;

    if (
      payload.contract_version !== 2 ||
      !Number.isFinite(serverTimeMs) ||
      !Number.isFinite(absoluteExpiresAtMs) ||
      !Number.isFinite(nextCheckAtMs) ||
      (warningPhase && !WARNING_PHASES.includes(warningPhase))
    ) return null;

    return {
      serverTimeMs,
      absoluteExpiresAtMs,
      nextCheckAtMs,
      warningPhase,
      receivedAtMs: performance.now(),
    };
  }

  remainingUntilMs(timestampMs, status = this.currentStatus) {
    if (!status) return 0;

    return timestampMs - status.serverTimeMs -
      (performance.now() - status.receivedAtMs);
  }

  absoluteRemainingMs(status = this.currentStatus) {
    if (!status) return 0;

    return this.remainingUntilMs(status.absoluteExpiresAtMs, status);
  }

  scheduleStatus(delayMs) {
    window.clearTimeout(this.statusTimer);
    this.statusTimer = window.setTimeout(
      this.requestStatus,
      Math.min(Math.max(delayMs, 0), MAX_STATUS_DELAY_MS)
    );
  }

  showWarning(state) {
    this.modalState = state;
    const minutesRemaining = Math.max(1, Math.ceil(this.absoluteRemainingMs() / 60_000));
    const content = buildSessionLifetimeWarningContent({state, minutesRemaining});

    if (document.getElementById(MODAL_ID)) {
      document.querySelector(`#${MODAL_ID} .js-modal-content`).innerHTML = content;
      this.configureModal();
    } else {
      this.previouslyFocusedElement = document.activeElement;
      document.dispatchEvent(new CustomEvent('sm-modal:open', {
        detail: {
          modalId: MODAL_ID,
          content,
          hideCloseButton: this.config.authorityNamespace === 'admin',
          onComplete: () => this.configureModal(),
        },
      }));
    }

    this.startCountdown();
  }

  showEnded() {
    window.clearTimeout(this.statusTimer);
    window.clearInterval(this.countdownTimer);
    this.currentStatus = null;

    if (document.getElementById(MODAL_ID) && this.modalState !== SESSION_WARNING_STATES.ENDED) {
      this.pendingEnded = true;
      this.closeWarning();
      return;
    }

    this.openEnded();
  }

  openEnded() {
    this.pendingEnded = false;
    this.modalState = SESSION_WARNING_STATES.ENDED;
    const content = buildSessionLifetimeWarningContent({state: SESSION_WARNING_STATES.ENDED});

    if (document.getElementById(MODAL_ID)) {
      document.querySelector(`#${MODAL_ID} .js-modal-content`).innerHTML = content;
      this.configureModal();
    } else {
      document.dispatchEvent(new CustomEvent('sm-modal:open', {
        detail: {
          modalId: MODAL_ID,
          content,
          hideCloseButton: true,
          onComplete: () => this.configureModal(),
        },
      }));
    }
  }

  configureModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    modal.setAttribute('aria-labelledby', SESSION_WARNING_TITLE_ID);
    modal.setAttribute('aria-describedby', SESSION_WARNING_DESCRIPTION_ID);
    modal.removeEventListener('keydown', this.handleWarningKeydown);
    modal.addEventListener('keydown', this.handleWarningKeydown);

    const dialog = modal.querySelector('[role="dialog"]');
    dialog?.classList.add('session-lifetime-warning-dialog');
    dialog?.setAttribute('aria-labelledby', SESSION_WARNING_TITLE_ID);
    dialog?.setAttribute('aria-describedby', SESSION_WARNING_DESCRIPTION_ID);

    modal.querySelector('.js-modal-content')
      ?.classList.add('session-lifetime-warning-dialog__content');
    modal.querySelector('.js-session-lifetime-sign-in-again')
      ?.addEventListener('click', () => this.submitSignOut());
    modal.querySelector('.js-session-lifetime-continue')
      ?.addEventListener('click', () => this.dismissWarning());

    this.focusPrimaryAction();
  }

  focusPrimaryAction() {
    const modal = document.getElementById(MODAL_ID);
    const primaryAction = modal?.querySelector('.js-session-lifetime-continue') ||
      modal?.querySelector('.js-session-lifetime-sign-in-again');

    primaryAction?.focus({preventScroll: true});
  }

  restorePreviousFocus() {
    const element = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    if (!element?.isConnected || typeof element.focus !== 'function') return;

    window.setTimeout(() => {
      if (element.isConnected) element.focus({preventScroll: true});
    }, 0);
  }

  startCountdown() {
    window.clearInterval(this.countdownTimer);
    this.updateCountdown();
    this.countdownTimer = window.setInterval(
      () => this.updateCountdown(),
      COUNTDOWN_INTERVAL_MS
    );
  }

  updateCountdown() {
    const remainingMs = this.absoluteRemainingMs();
    if (remainingMs <= 0) {
      window.clearInterval(this.countdownTimer);
      this.requestStatus();
      return;
    }

    const countdown = document.querySelector(`#${MODAL_ID} .js-session-lifetime-countdown`);
    if (!countdown) return;

    countdown.textContent = sessionLifetimeRemainingLabel(
      Math.max(1, Math.ceil(remainingMs / 60_000))
    );
  }

  submitSignOut() {
    const form = document.createElement('form');
    const signOutMethod = this.config.signOutMethod.toLowerCase();
    form.hidden = true;
    form.method = signOutMethod === 'get' ? 'get' : 'post';
    form.action = this.config.signOutUrl;

    if (signOutMethod !== 'get') {
      this.appendFormValue(form, '_method', signOutMethod);
      if (this.csrfToken) this.appendFormValue(form, 'authenticity_token', this.csrfToken);
    }
    this.appendFormValue(form, 'sign_in_again', '1');
    this.appendFormValue(
      form,
      'return_to',
      this.config.returnTo || window.location.pathname
    );

    document.body.append(form);
    form.submit();
  }

  appendFormValue(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.append(input);
  }

  dismissalKey(phase) {
    return [
      'session-lifetime-warning',
      this.config.authorityNamespace,
      this.currentStatus?.absoluteExpiresAtMs,
      phase,
    ].join(':');
  }

  dismissed(phase) {
    try {
      return window.sessionStorage.getItem(this.dismissalKey(phase)) === 'dismissed';
    } catch {
      return false;
    }
  }

  rememberDismissal(phase) {
    try {
      window.sessionStorage.setItem(this.dismissalKey(phase), 'dismissed');
    } catch {
      // A blocked storage API must not change server authority or break the modal.
    }
  }

  dismissWarning() {
    if (!document.getElementById(MODAL_ID)) return;

    document.dispatchEvent(new CustomEvent('sm-modal:close', {
      detail: {modalId: MODAL_ID},
    }));
  }

  closeWarning() {
    window.clearInterval(this.countdownTimer);
    if (!document.getElementById(MODAL_ID)) {
      this.modalState = null;
      if (this.pendingEnded) this.openEnded();
      return;
    }

    this.suppressDismissalOnClose = true;
    document.dispatchEvent(new CustomEvent('sm-modal:close', {
      detail: {modalId: MODAL_ID},
    }));
  }

  stop() {
    window.clearTimeout(this.statusTimer);
    window.clearInterval(this.countdownTimer);
    this.pendingStatusRequest = false;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('sm-modal:closed', this.handleModalClosed);
    window.removeEventListener('focus', this.requestStatus);
  }

  handleVisibilityChange() {
    if (!document.hidden) this.requestStatus();
  }

  handleWarningKeydown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    if (this.modalState === SESSION_WARNING_STATES.ENDED) {
      this.focusPrimaryAction();
    } else if (WARNING_PHASES.includes(this.modalState)) {
      this.dismissWarning();
    }
  }

  handleModalClosed(event) {
    if (event.detail?.modalId !== MODAL_ID || !this.modalState) return;

    const closedState = this.modalState;
    const userDismissedWarning = !this.suppressDismissalOnClose &&
      WARNING_PHASES.includes(closedState);
    this.modalState = null;
    window.clearInterval(this.countdownTimer);

    if (this.suppressDismissalOnClose) {
      this.suppressDismissalOnClose = false;
    } else if (userDismissedWarning) {
      this.rememberDismissal(closedState);
    }

    if (this.pendingEnded) {
      this.previouslyFocusedElement = null;
      this.openEnded();
    } else {
      if (userDismissedWarning) {
        this.restorePreviousFocus();
      } else {
        this.previouslyFocusedElement = null;
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const configElement = document.querySelector(CONFIG_SELECTOR);
  if (configElement) new SessionLifetimeWarning(configElement).start();
});
